import React, { startTransition, useEffect, useMemo } from "react";
import { find, findIndex } from "lodash";

import Tabs, { Tab, TabPanel } from "@ingka/tabs";
import plus from "@ingka/ssr-icon/paths/plus";

import { isCategorization, rankWith, StatePropsOfLayout } from "@jsonforms/core";
import { JsonFormsDispatch, withJsonFormsLayoutProps } from "@jsonforms/react";

import { useCategorizationService, useSearchElementParents, useSelection } from "../../core/context";
import { CategorizationLayout, EditorCategoryElement } from "../model/uischema";
import { createCategory } from "../util/generators/uiSchema";
import { DroppableElementRegistration } from "./DroppableElement";

interface DroppableCategorizationLayoutProps extends StatePropsOfLayout {
	uischema: CategorizationLayout;
}

const DroppableCategorizationLayout: React.FC<DroppableCategorizationLayoutProps> = (props) => {
	const { uischema, schema, path, renderers, cells } = props;

	// ignoring the first selection from the tuple since it is not used
	const [, setSelection] = useSelection();
	const categorizationService = useCategorizationService();
	const [searchElementParent] = useSearchElementParents();

	const categories = uischema.elements;

	const defaultCategory = find(
		categories,
		(cat) => cat.uuid === categorizationService.getTabSelection(uischema)?.uuid
	)?.uuid;

	const defaultIndex = findIndex(
		categories,
		(cat) => cat.uuid === categorizationService.getTabSelection(uischema)?.uuid
	);

	const [currentSelectedTab, setCurrentSelectedTab] = React.useState<string | undefined>(defaultCategory);

	const setSelectedTab = React.useCallback(
		(selectedUuid: string, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			event?.stopPropagation();

			categorizationService.setTabSelection(uischema, {
				uuid: selectedUuid,
			});
			setSelection({ uuid: selectedUuid });
		},
		[categorizationService, setSelection, uischema]
	);

	// DroppableControl removed itself before dispatching to us, we need
	// to re-add it for our children
	const renderersToUse = useMemo(() => {
		return renderers && [...renderers, DroppableElementRegistration];
	}, [renderers]);

	const handleChange = React.useCallback(
		(value: string, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			if (typeof value === "string") {
				const targetCategory = categories.find((cat) => cat.uuid === value);

				if (!targetCategory) {
					console.log("Invalid category UUID:", value);
					return;
				}
				if (currentSelectedTab === value) return;

				setCurrentSelectedTab(value);
				startTransition(() => {
					setSelectedTab(value, event);
				});
			}
		},
		[setSelectedTab, categories, currentSelectedTab]
	);

	React.useEffect(() => {
		categories.forEach((el) => {
			const elementUuid = el.uuid;
			if (searchElementParent.includes(elementUuid)) {
				setCurrentSelectedTab(elementUuid);
				//maybe needed later
				//setSelectedTab(elementUuid);
			}
		});
	}, [categories, searchElementParent, uischema.uuid]);

	const addTab = React.useCallback(
		(event?: React.MouseEvent<HTMLButtonElement>) => {
			const tab = createCategory("New Tab " + (categories.length + 1));
			tab.parent = uischema;
			categories.push(tab);
			handleChange(categories[categories.length - 1].uuid, event);
		},
		[categories, handleChange, uischema]
	);

	const categoriesTabs = React.useMemo(() => {
		const tabs = categories.map((e: EditorCategoryElement, idx: number) => (
			<Tab
				data-testid={`category-tab-${idx}`}
				key={idx}
				text={e.label}
				tabPanelId={e.uuid}
				onClick={(ev) => {
					handleChange(e.uuid, ev);
				}}
			/>
		));
		return [
			...tabs,
			<Tab
				data-testid="add-new-tab"
				key={categories.length + 1}
				ssrIcon={plus}
				text="Add new tab"
				onClick={addTab}
				tabPanelId={`tabpanel-${categories.length + 1}`}
			/>,
		];
	}, [addTab, categories, handleChange]);

	useEffect(() => {
		if (currentSelectedTab !== undefined) {
			const currentIndex = findIndex(categories, (cat) => cat.uuid === currentSelectedTab);

			// in case we have tab that was deleted then we will use the memorized index to determine the previous tab that we are going to select automatically
			if (categories.length === 0) {
				// reset the index since we do not have anything to select
				setCurrentSelectedTab(undefined);
			} else if (currentIndex === -1) {
				// check if currentIndex is not found because of delete
				handleChange(categories[categories.length - 1].uuid);
			} else if (currentIndex !== defaultIndex) {
				// check if current index is out of sync with the service
				handleChange(categories[currentIndex].uuid);
			}
		}
	}, [categories, currentSelectedTab, defaultIndex, handleChange]);

	return (
		<>
			{categories && (
				<Tabs
					activeTab={currentSelectedTab}
					tabs={categoriesTabs}
					tabPanels={categories.map((e: EditorCategoryElement, idx: number) => (
						<TabPanel tabPanelId={e.uuid}>
							<JsonFormsDispatch
								schema={schema}
								uischema={categories[idx]}
								path={path}
								renderers={renderersToUse}
								cells={cells}
							/>
						</TabPanel>
					))}
				></Tabs>
			)}
		</>
	);
};

export const DroppableCategorizationLayoutRegistration = {
	tester: rankWith(40, isCategorization), // less than DroppableElement
	renderer: withJsonFormsLayoutProps(DroppableCategorizationLayout as React.FC<StatePropsOfLayout>),
};
